import uiConfig from 'src/lib/ui/config';
import { View } from '@play-co/timestep-core/ui';
import ResponsiveScene from './ResponsiveScene';
import { Transition } from 'src/game/logic/SceneManager';
import { waitForItPromise } from 'src/lib/utils';
import { isSceneEntered } from 'src/lib/stateUtils';
import statePromise from 'src/lib/statePromise';
import StateObserver, { user } from 'src/StateObserver';
import i18n from 'src/lib/i18n/i18n';
import { endVisitSession } from '../visit/visit.sequence';
import { getFriendsPet, getPet } from '../pet';
import * as Duo from 'src/replicant/duo';
import { Feature } from 'src/replicant/analytics';
import { SideButton } from '../components/SideButton';
import features from 'src/replicant/ruleset/features';
import { startDuoInviteSequence } from '../duo/duo.sequence';
import { LockedReason } from '../popups/PopupFeatureDisabled';
import { createEmitter } from 'src/lib/Emitter';
import LivingRoomDecorationView from '../decoration/LivingRoomDecorationView';
import type {
  DecorationItem,
  DecorationSlot,
} from 'src/replicant/decoration/decoration.ruleset';
import { debugBreadcrumb } from 'src/lib/sentry';
import { AssetGroup } from '@play-co/timestep-core';

export default class FriendsLivingRoomScene extends ResponsiveScene {
  private goHomeButton!: SideButton;
  private duoButton!: SideButton;
  private decorationView: LivingRoomDecorationView;

  constructor() {
    super(new View({ width: uiConfig.width, height: uiConfig.height }));
    this.decorationView = new LivingRoomDecorationView({
      autoDecorationUpdate: false,
    });
    this.getView().addSubview(this.decorationView);

    this.getView().tag = 'friendsLivingRoom';

    this.createGoHomeButton();
    this.createDuoButton();
  }

  public async preload() {
    await new AssetGroup(AssetGroup.constructURLs(['assets/ui/visit/'])).load();
  }

  /**
   * Called before scene show
   * Show friend room decorations
   */
  public prepareScene() {
    // Clear all previous
    this.decorationView.clear();

    // Try to get decorations
    const decorations: Map<DecorationSlot, DecorationItem> =
      getFriendsPet()?.getRoomDecoration('living') ?? new Map();

    // Set friends decorations assets
    this.decorationView.updateSelectedItems(decorations, 'classic');
  }

  public async showScene() {
    // If pet is revived show pet revived speech
    const { revivedPet } = user().visit.visitSession ?? {};
    if (revivedPet) {
      await getFriendsPet()?.startConversation('friend_petrevived');
    }

    await statePromise(() => isSceneEntered('friendsLivingRoom'));
    await waitForItPromise(500);
    getPet().position.setWandering(true);
    getFriendsPet()?.position.setWandering(true);
  }

  public hideScene() {
    getPet().position.setWandering(false);
    getFriendsPet()?.position.setWandering(false);
  }

  public transition(): Transition {
    const { ui } = StateObserver.getState();

    return ui.transition.previousScene !== 'livingRoom'
      ? 'slide_back'
      : 'slide_forward';
  }

  private createGoHomeButton() {
    this.goHomeButton = new SideButton({
      superview: this.getView(),
      type: 'visitFriend',
      x: 619,
      y: 224,
      onClick: () => this.goHome(),
      label: i18n('friendsVisit.livingRoom.goHome'),
      icon: {
        image: 'assets/ui/visit/icons/home_icon.png',
      },
      tracking: {
        feature: 'friends-visit',
        $subFeature: 'friends-visit-go-home',
      },
    });
  }

  private async goHome() {
    // Do not show menu if conversation is not finished
    if (getPet().conversation.inProgress()) return;

    // If menu is locked that mean we are in exit sequence now
    const state = StateObserver.getState();
    if (state.ui.lockMenu) return;

    const { friendId } = user().visit.visitSession ?? {};
    if (!friendId) {
      return;
    }

    // Start goodby conversation
    await getPet().startConversation('friend_goodby');
    await getFriendsPet()?.startConversation('friend_farewell');

    // End visit session.
    const { visitSession } = user().visit;
    if (visitSession) {
      debugBreadcrumb('Home button end session');
      await endVisitSession({ friendId, sendMessage: true });
    }
  }

  private createDuoButton() {
    this.duoButton = new SideButton({
      superview: this.getView(),
      x: 619,
      y: 396,
      type: 'duo',
      onClick: async () => {
        // Menu actions is locked
        if (StateObserver.getState().ui.lockMenu) return;

        await startDuoInviteSequence();
      },
      label: i18n('friendsVisit.livingRoom.formDuo'),
      icon: {
        image: 'assets/ui/duo/icons/duo_icon.png',
        width: 140,
        height: 117,
        offsetX: 10,
        offsetY: -5,
      },
      tracking: {
        feature: Feature.DUO._,
        $subFeature: Feature.DUO.FORM_DUO,
      },
    });

    const {
      isUnlocked,
      hasPendingSentInvitations,
      hasPendingReceivedInvitations,
    } = Duo.getters;

    createEmitter(this.duoButton, ({ user }) => ({
      enabled: features(user).duo,
      locked: !isUnlocked(user),
      pendingSentInvitations: hasPendingSentInvitations(
        user,
        StateObserver.now(),
      ),
      pendingReceivedInvitations: hasPendingReceivedInvitations(
        user,
        StateObserver.now(),
      ),
      areBothPetsAdults:
        getPet().stage === 'adult' && getFriendsPet()?.stage === 'adult',
      dying: getPet().state === 'dying' || getFriendsPet()?.state === 'dying',
    })).addListener(
      ({
        enabled,
        locked,
        pendingSentInvitations,
        pendingReceivedInvitations,
        areBothPetsAdults,
        dying,
      }) => {
        // Hide the button if the feature is disabled.
        this.duoButton.updateOpts({
          visible: enabled,
        });

        // Lock button if the feature is not unlocked or unavailable.
        const shouldBeLocked =
          locked ||
          pendingSentInvitations ||
          pendingReceivedInvitations ||
          !areBothPetsAdults ||
          dying;

        let reason: LockedReason = 'generic';
        if (pendingSentInvitations) {
          reason = 'pendingInvitation';
        } else if (pendingReceivedInvitations) {
          reason = 'pendingResponse';
        } else if (!areBothPetsAdults) {
          reason = 'notAdult';
        } else if (dying) {
          reason = 'dead';
        }

        this.duoButton.setLocked(shouldBeLocked, reason);
      },
    );
  }
}
